'use client';

import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import type { LoadingContent } from '@/shared/lib/loading-content';
import type { LoadingFactItem } from './use-loading-facts';
import { seenFactsBatcher } from '../api/seen-facts-batcher';
import {
  getLoadingContentTracker,
  getUserStats,
} from '@/shared/lib/loading-content-tracker';

export interface UseLoadingContentOptions {
  isActive: boolean;
  trackRender?: boolean;
  contextualFacts?: LoadingFactItem[];
  seenIds?: string[];
  /**
   * Server-driven switch (loading_facts_enabled). Heavy unlimited accounts get
   * false — we then never surface facts (progress/stages still render).
   */
  enabled?: boolean;
}

export interface LoadingContentState {
  currentContent: LoadingContent | null;
  userStats: {
    renderCount: number;
    sessionDuration: number;
  };
  markContentSeen: (contentId: string) => void;
  getNextContent: () => void;
}

/** How long a fact stays on screen before the blank pause before the next one. */
const FACT_VISIBLE_MS = 20000;
/** Min on-screen time before a fact counts as "viewed" and is recorded server-side. */
const VIEWED_THRESHOLD_MS = 8000;

/**
 * Blank-pause length after a fact, growing with how many facts we've already
 * shown this render session: dense at first, then progressively sparser. Total
 * cadence (visible + gap) ramps 25s → 40s → 60s. Heavy re-renderers stop reading
 * the facts, so easing off keeps the screen calmer instead of flipping quotes
 * relentlessly. Kept identical across web/iOS/Android.
 */
function gapAfter(shownIndex: number): number {
  if (shownIndex < 3) return 5000;
  if (shownIndex < 6) return 20000;
  return 40000;
}

export function useLoadingContent({
  isActive,
  trackRender = false,
  contextualFacts,
  seenIds,
  enabled = true,
}: UseLoadingContentOptions): LoadingContentState {
  const [currentContent, setCurrentContent] = useState<LoadingContent | null>(
    null
  );
  const scheduleRef = useRef<NodeJS.Timeout | null>(null);
  const tracker = getLoadingContentTracker();
  const hasTrackedRender = useRef(false);
  // Facts surfaced this session (drives the pause ramp) and the ids used in the
  // current recycle cycle. When every fact has been shown we clear usedIds and
  // cycle again — we no longer ask the server to regenerate on exhaustion.
  const shownCountRef = useRef(0);
  const usedIdsRef = useRef<Set<string>>(new Set());

  // Merge server-side seen IDs into the local tracker (feeds the avoid-list on
  // the next real generation). Display no longer filters by seen — we recycle
  // the pool locally — so this is purely for cross-device seen bookkeeping.
  useEffect(() => {
    if (seenIds && seenIds.length > 0) {
      for (const id of seenIds) {
        tracker.markContentSeen(id);
      }
    }
  }, [seenIds, tracker]);

  // Build contextual content from API facts, using API-provided IDs
  const contextualContent = useMemo((): LoadingContent[] => {
    if (!contextualFacts || contextualFacts.length === 0) return [];
    return contextualFacts.map((item) => ({
      id: item.id,
      type: item.type as LoadingContent['type'],
      text: item.text,
    }));
  }, [contextualFacts]);

  // Live pool ref so the self-scheduling rotation always reads the freshest
  // facts (WS batches) without tearing down and restarting the timer.
  const poolRef = useRef<LoadingContent[]>(contextualContent);
  useEffect(() => {
    poolRef.current = contextualContent;
  }, [contextualContent]);

  // Pick the next fact, recycling once every fact in the pool has been used.
  const pickNext = useCallback((): LoadingContent | null => {
    const pool = poolRef.current;
    if (pool.length === 0) return null;
    let available = pool.filter((c) => !usedIdsRef.current.has(c.id));
    if (available.length === 0) {
      usedIdsRef.current.clear(); // recycle — start the pool over
      available = pool;
    }
    const chosen =
      available[Math.floor(Math.random() * available.length)] ?? null;
    if (chosen) usedIdsRef.current.add(chosen.id);
    return chosen;
  }, []);

  // Mark current fact as "viewed" (and persist server-side) only after it has
  // been on screen ≥8s. Cleared by the next currentContent change / unmount.
  useEffect(() => {
    if (!currentContent) return;
    const id = currentContent.id;
    const timer = setTimeout(() => {
      tracker.markContentSeen(id);
      seenFactsBatcher.enqueue(id);
    }, VIEWED_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, [currentContent, tracker]);

  // Track render count once
  useEffect(() => {
    if (trackRender && isActive && !hasTrackedRender.current) {
      tracker.incrementRenderCount();
      hasTrackedRender.current = true;
    }
  }, [trackRender, isActive, tracker]);

  // Self-scheduling rotation: show a fact for FACT_VISIBLE_MS, blank it, wait a
  // gap that grows with how many we've shown, then surface the next. Owns its
  // own cadence so WS pool updates never restart the countdown. Runs only while
  // active and enabled (heavy unlimited accounts get enabled=false → silent).
  const hasPool = contextualContent.length > 0;
  useEffect(() => {
    const clear = () => {
      if (scheduleRef.current) {
        clearTimeout(scheduleRef.current);
        scheduleRef.current = null;
      }
    };

    if (!isActive || !enabled || !hasPool) {
      clear();
      setCurrentContent(null);
      return;
    }

    const showNext = () => {
      const next = pickNext();
      if (!next) {
        scheduleRef.current = setTimeout(showNext, 2000); // pool not ready yet
        return;
      }
      setCurrentContent(next);
      const gap = gapAfter(shownCountRef.current);
      shownCountRef.current += 1;
      scheduleRef.current = setTimeout(() => {
        setCurrentContent(null); // blank pause between facts
        scheduleRef.current = setTimeout(showNext, gap);
      }, FACT_VISIBLE_MS);
    };

    showNext();
    return clear;
  }, [isActive, enabled, hasPool, pickNext]);

  // Flush pending seen IDs on unmount
  useEffect(() => {
    return () => {
      seenFactsBatcher.flushNow();
    };
  }, []);

  // Manual content selection — jump to the next fact immediately, keeping the
  // ramp/pause scheduler intact. Setting currentContent restarts the 8s
  // viewed-threshold timer in the effect above; we don't mark seen here.
  const getNextContent = useCallback(() => {
    if (!enabled) return;
    const next = pickNext();
    if (next) setCurrentContent(next);
  }, [enabled, pickNext]);

  // Mark content as seen
  const markContentSeen = useCallback(
    (contentId: string) => {
      tracker.markContentSeen(contentId);
      seenFactsBatcher.enqueue(contentId);
    },
    [tracker]
  );

  return {
    currentContent,
    userStats: getUserStats(),
    markContentSeen,
    getNextContent,
  };
}

/**
 * Hook to get localized content type labels from render.json translations.
 */
export function useLoadingLabels() {
  const t = useTranslations('render.loadingLabels');
  return useMemo(
    () => ({
      didYouKnow: t('didYouKnow'),
      designTip: t('designTip'),
      buildingTrivia: t('buildingTrivia'),
    }),
    [t]
  );
}
